import * as React from "react";
import { useLocation } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import { switchTarget } from "../dashboard/select";
import useDashboardCtx from "../dashboard/useDashboardCtx";
import { useActiveInfluencer } from "../services/ActiveInfluencer";
import fetcher from "./fetcher";
import useDebounce from "./useDebounce";

const SHOPS_ENDPOINT = "/api/dashboard/shops";

const isShopsKey = (key) =>
  typeof key === "string" && key.startsWith(SHOPS_ENDPOINT);

const requestPin = async ({ influencerId, pinned }) => {
  const res = await window.fetch(`${SHOPS_ENDPOINT}/${influencerId}/pin`, {
    method: pinned ? "PUT" : "DELETE",
  });
  if (!res.ok) throw new Error(`Couldn't update the pin (${res.status})`);
};

export default function useShopSwitcher() {
  const { pathname } = useLocation();
  const activeInfluencer = useActiveInfluencer();
  const { mutate } = useSWRConfig();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");

  useDebounce(() => setDebouncedQuery(searchQuery.trim()), 200, [searchQuery]);

  const { data, error } = useSWR(
    `${SHOPS_ENDPOINT}?${new URLSearchParams({ q: debouncedQuery })}`,
    fetcher,
    { keepPreviousData: true },
  );

  const ctx = useDashboardCtx();
  const pathFor = React.useCallback(
    (shop) => switchTarget({ currentPath: pathname, shop, ctx }),
    [pathname, ctx],
  );

  const setPinned = React.useCallback(
    async (shop, pinned) => {
      await requestPin({ influencerId: shop.influencerId, pinned });
      await mutate(isShopsKey);
    },
    [mutate],
  );

  return {
    activeInfluencer,
    searchQuery,
    setSearchQuery,
    isSearching: debouncedQuery !== "",
    pinned: data?.pinned ?? [],
    recent: data?.recent ?? [],
    results: data?.results ?? [],
    isLoading: !data && !error,
    error,
    pathFor,
    setPinned,
  };
}
